import { useTranslation } from "react-i18next";
import TimelineComponent from "./timeline/timeline";
import { AdminFormFieldInput } from "../../../../components/admin/formFields";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DeviceEvent,
  DeviceEventsListPayload,
  DeviceStorageListingPayload,
} from "../../../../api/types/Camera";
import {
  useGetCameraConfigQuery,
  useGetCameraDetailsQuery,
  useGetDeviceStorageListingQuery,
  useLazyGetDeviceStorageListingQuery,
} from "../../../../api/network/vmApiService";
import EventDetails from "./event-list";
import { isToday, parse } from "date-fns";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  get30DaysEarlierDateInUTC,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../../../utils/dateTime";
import { APP_CONFIG } from "../../../../constants/constants";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  convertToTZForDateFnsNoHook,
  useAccountTimezone,
} from "../../../../hooks/timezone";
import { VideoList } from "../../../../api/types/CamerasAdminVM";
import { useGetDeviceEventsListQuery } from "../../../../api/network/camerasAdminVMApiService";
import { CAMERA_CAPTURE_MODES } from "../../constants";

interface TimelineSelectorProps {
  cameraId: string;
  serial_num: string;
}

const TimelineSelector = (props: TimelineSelectorProps) => {
  const { cameraId, serial_num } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.video_history.video_playback",
  });
  const { tzForDateFns } = useAccountTimezone();
  const [selectedEvent, setSelectedEvent] = useState<DeviceEvent | null>(null);
  const [selectedTimelineData, setSelectedTimelineData] = useState<
    VideoList[] | null
  >(null);
  const [selectedTime, setSelectedTime] = useState<number>();
  const [customSelectedTime, setCustomSelectedTime] = useState<number>();
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(
    new Date() // Default to current date
  );
  const [availableVideoDates, setAvailableVideoDates] = useState<string[]>([]);

  // Get Device config
  const { data: cameraConfigResponse, isLoading } =
    useGetCameraConfigQuery(cameraId);
  const cameraConfig = cameraConfigResponse?.data ?? null;

  // Get camera details
  const { data: deviceDetailRespsonse, isLoading: isLoadingDeviceData } =
    useGetCameraDetailsQuery(cameraId ?? "", {
      skip: !cameraId,
    });

  const dataDeviceDetails =
    deviceDetailRespsonse?.data && deviceDetailRespsonse?.data?.length > 0
      ? deviceDetailRespsonse?.data[0]
      : null;

  // Get the date in accout timezone
  const selectedDate = useMemo(
    () =>
      formattedDateTimeBasedOnTimezone(
        new Date(),
        convertToTZForDateFnsNoHook(tzForDateFns),
        "yyyy-MM-dd"
      ),
    [tzForDateFns]
  );
  // Get full date with time in account timezone
  const selectedFullDate = useMemo(
    () =>
      formattedDateTimeBasedOnTimezone(
        new Date(),
        convertToTZForDateFnsNoHook(tzForDateFns)
      ),
    [tzForDateFns]
  );
  // Get UTC dates based on account timezone date
  const { startOfDayInUTC, endOfDayInUTC } = useMemo(
    () =>
      getStartEndOfDayInUTC(
        selectedDate,
        convertToTZForDateFnsNoHook(tzForDateFns)
      ),
    [tzForDateFns, selectedDate]
  );
  const { earlierDate } = get30DaysEarlierDateInUTC(
    selectedDate,
    convertToTZForDateFnsNoHook(tzForDateFns)
  );
  // Find current date time in UTC
  const utcDate = useMemo(
    () =>
      fromZonedTime(
        parse(selectedFullDate, "LLL dd, yyyy h:mmaa", new Date()),
        convertToTZForDateFnsNoHook(tzForDateFns)
      ),
    [selectedFullDate, tzForDateFns]
  );

  const [storageApiTrigger] = useLazyGetDeviceStorageListingQuery();

  /**
   * Method to retrieve last 30 days record of dates having video recording
   */
  useEffect(() => {
    if (!cameraId) {
      return;
    }
    storageApiTrigger({
      cid: cameraId,
      time_format: "timestamp",
      start_timestamp: jsTimeToDBTime(earlierDate.getTime()), // Convert to epoch seconds
      end_timestamp: jsTimeToDBTime(utcDate.getTime()), // Convert to epoch seconds
    }).then((res) => {
      if (res && res.data) {
        const storageListingResponse = res.data?.data;
        if (storageListingResponse && storageListingResponse?.length > 0) {
          const storageList: VideoList[] = [];
          storageListingResponse?.forEach((item) => {
            storageList.push(...item.list);
          });
          // Get dates in "yyyy-MM-dd" format from list of vide storage records
          const distinctDates = Array.from(
            new Set(
              (storageList || []).map((item: VideoList) =>
                formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(item.time ?? 0),
                  tzForDateFns,
                  "yyyy-MM-dd"
                )
              )
            )
          );
          setAvailableVideoDates(distinctDates);
        }
      }
    });
  }, [cameraId]);

  const [storageQueryParams, setStorageQueryParams] =
    useState<DeviceStorageListingPayload>({
      cid: cameraId,
      // start_timestamp: jsTimeToDBTime(startOfDayInUTC.getTime()),
      // end_timestamp: jsTimeToDBTime(utcDate.getTime()),
      // calendar_end_time: jsTimeToDBTime(endOfDayInUTC.getTime()),
      start_timestamp: 0,
      end_timestamp: 0,
      calendar_end_time: 0,
    });
  useEffect(() => {
    setStorageQueryParams({
      cid: cameraId,
      start_timestamp: jsTimeToDBTime(startOfDayInUTC.getTime()),
      end_timestamp: jsTimeToDBTime(utcDate.getTime()),
      calendar_end_time: jsTimeToDBTime(endOfDayInUTC.getTime()),
    });
  }, [startOfDayInUTC, utcDate, endOfDayInUTC, cameraId]);
  /**
   * Get device storage listing based on the cameraId and storageQueryParams
   */
  const { data: dataDeviceStorage } = useGetDeviceStorageListingQuery(
    {
      cid: cameraId,
      time_format: "timestamp",
      start_timestamp: storageQueryParams.start_timestamp, // Convert to epoch seconds
      end_timestamp: storageQueryParams.end_timestamp, // Convert to epoch seconds
    },
    {
      skip: !cameraId, // Skip query if cameraId is not provided

      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  /**
   * Create new list of sd card storage with 90 sec. duration instead of 180.
   * This is added to keep the clicks on timeline as close as possible
   */
  const videoStorageList: VideoList[] = useMemo(() => {
    if (dataDeviceStorage && dataDeviceStorage?.data?.length > 0) {
      const videoList: VideoList[] = [];
      dataDeviceStorage?.data?.forEach((item) => {
        // Add camera capture mode for each recording data(To be used while requesting video retrieval)
        const sdCardData: VideoList[] = item.list.map((videoItem) => {
          return {
            ...videoItem,
            captureMode: item.capture_mode as CAMERA_CAPTURE_MODES,
          };
        });
        videoList.push(...sdCardData);
      });
      // Divide all the sd card storage data(default: 180 sec each) to small er group for better click experience on timeline
      const sdCardStorageItems: VideoList[] = [];
      videoList.forEach((event, idx) => {
        const itemPart1: VideoList = {
          time: event.time,
          duration: Math.round(event.duration / 2),
          dst: event.dst,
          captureMode: event.captureMode,
        };
        const itemPart2 = {
          time: event.time + Math.round(event.duration / 2),
          duration: Math.round(event.duration / 2),
          dst: event.dst,
          captureMode: event.captureMode,
        };
        sdCardStorageItems.push(itemPart1, itemPart2);
      });
      return sdCardStorageItems;
    } else {
      return [];
    }
  }, [dataDeviceStorage]);

  const handleDateChange = (date: any) => {
    setSelectedCalendarDate(date);
    const updatedDate = formattedDateTimeBasedOnTimezone(
      new Date(date),
      tzForDateFns,
      "yyyy-MM-dd"
    );
    const currentDateTime = formattedDateTimeBasedOnTimezone(
      new Date(),
      tzForDateFns
    );
    const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(
      updatedDate,
      tzForDateFns
    );
    const currentTimeUTC = fromZonedTime(
      parse(currentDateTime, "LLL dd, yyyy h:mmaa", new Date()),
      tzForDateFns
    );
    let isCurrentDate = isToday(new Date(updatedDate));
    const endTime = !!isCurrentDate
      ? jsTimeToDBTime(currentTimeUTC.getTime())
      : jsTimeToDBTime(endOfDayInUTC.getTime());
    setStorageQueryParams((prevParams) => ({
      ...prevParams,
      start_timestamp: jsTimeToDBTime(startOfDayInUTC.getTime()),
      end_timestamp: endTime, // If today, set to current time, else to end of day
      calendar_end_time: jsTimeToDBTime(endOfDayInUTC.getTime()),
    }));
  };

  const [deviceEventsQueryParams, setDeviceEventsQueryParams] =
    useState<DeviceEventsListPayload>({
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      page: 1,
      serial_number: "",
      statuscodes: "",
      start_time: storageQueryParams.start_timestamp,
      end_time: storageQueryParams.end_timestamp,
    });

  const { data: dataDeviceEventsList, isFetching: isFetchingDeviceEventsList } =
    useGetDeviceEventsListQuery(
      {
        ...deviceEventsQueryParams,
        page: 1,
        page_size: 1000,
        serial_number: serial_num || "",
        start_time: storageQueryParams.start_timestamp,
        end_time: storageQueryParams.end_timestamp,
      },
      {
        skip: !serial_num,
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  useEffect(() => {
    // Reset selection on camera change
    setSelectedTimelineData(null);
    setSelectedTime(0);
    if (
      dataDeviceEventsList?.results?.length &&
      dataDeviceEventsList?.results?.length > 0
    ) {
      setSelectedEvent(dataDeviceEventsList?.results?.[0] ?? null);
      const localStart = toZonedTime(
        dbTimeToJsTime(dataDeviceEventsList?.results?.[0]?.timestamp),
        convertToTZForDateFnsNoHook(tzForDateFns)
      );
      setSelectedTime(localStart.getTime());
    } else {
      setSelectedEvent(null);
    }
  }, [serial_num, cameraId, dataDeviceStorage, dataDeviceEventsList]);

  const onTimelineClick = (videoItem: VideoList[], time: number) => {
    setSelectedEvent(null); // Reset any selected camera event
    setSelectedTimelineData(videoItem);
    setSelectedTime(time);
  };

  const onCustomTimeSelected = (time: number) => {
    if (time && time !== selectedTime) {
      setCustomSelectedTime(time);
    }
  };

  const onCameraEventClick = (event: DeviceEvent, time: number) => {
    setSelectedTimelineData(null); // Remove any selected video timeline
    // Handle event selection if needed
    if (event?.ticket !== selectedEvent?.ticket) {
      setSelectedEvent(event);
    }
    setSelectedTime(time);
  };

  console.log("stora", storageQueryParams);

  return (
    <div className="w-full">
      <div className="w-full Xgrid Xgrid-cols-12 flex row-between gap-2">
        <AdminFormFieldInput
          customWrapperClass="col-span-6"
          label={""}
          type="date-picker"
          id={`date_time`}
          name={`date_picker`}
          value={selectedCalendarDate ?? ""}
          maxTime={new Date()}
          onDatepickerChange={(fieldName: string, value: any) => {
            handleDateChange(value);
          }}
          // onBlur={handleBlur}
          // error={errors.date_time}
          // touched={touched.date_time}
          detailsFormField={true}
          size="lg"
          highlightedDates={availableVideoDates}
        />
        {/* <AdminFormFieldSubmit
          buttonDimensionClass={"h-11 w-full px-2 group"}
          customWrapperClass="w-28 mt-1"
          type="button"
          variant="primary"
          label={
            <span className="flex justify-center whitespace-nowrap">
              {t("clear")}
              {
                <img
                  className={`ml-2 ${!isDisabled ? "transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert" : ""} h-5 w-5`}
                  src={isDisabled ? svgIcons.CloseIconGray : svgIcons.CloseIcon}
                  alt={"clear"}
                />
              }
            </span>
          }
          onClick={() => {
          }}
          disabled={isDisabled}
        /> */}
      </div>
      {!!storageQueryParams.start_timestamp &&
        !!storageQueryParams.calendar_end_time &&
        !!storageQueryParams.end_timestamp && (
          <TimelineComponent
            videoList={videoStorageList ?? []}
            eventList={
              dataDeviceEventsList?.results &&
              dataDeviceEventsList?.results?.length > 0
                ? dataDeviceEventsList?.results
                : []
            }
            startTime={storageQueryParams.start_timestamp}
            endTime={storageQueryParams.calendar_end_time ?? 0}
            // onEventSelect={onCameraEventClick}
            onTimelineClick={onTimelineClick}
            selectedEvent={selectedEvent}
            customSelectedTime={customSelectedTime ?? 0}
          />
        )}
      <EventDetails
        eventList={dataDeviceEventsList?.results || []}
        selectedEvent={selectedEvent}
        selectedTimelineData={selectedTimelineData}
        onEventSelect={(event) => {
          if (event?.recordid !== selectedEvent?.recordid) {
            setSelectedEvent(event);
            const localStart = toZonedTime(
              dbTimeToJsTime(event?.timestamp),
              convertToTZForDateFnsNoHook(tzForDateFns)
            );
            setSelectedTime(localStart.getTime());
          }
          setSelectedTimelineData(null);
        }}
        serial_num={serial_num}
        cameraId={cameraId}
        selectedTime={selectedTime ?? 0}
        config={cameraConfig}
        cameraDetail={dataDeviceDetails}
        captureMode={
          dataDeviceStorage && dataDeviceStorage?.data?.length > 0
            ? dataDeviceStorage?.data[0]?.capture_mode
            : null
        }
        dataDeviceStorage={dataDeviceStorage?.data}
        onCustomTimeSelected={onCustomTimeSelected}
      />
    </div>
  );
};
export default TimelineSelector;
